// Optimistic edits of a cached identity detail (ADR-0039, slice 03).
//
// A decision removes the row and a reference toggle flips the strip before the
// request answers; the response then replaces the whole detail anyway, so these
// only have to keep the table and the counts honest in the meantime. Whether the
// other candidates keep their *suggested match* label is the API's call
// (ADR-0040) — it arrives with the response, not guessed here.
import type {
  FaceMatchingIdentityDetail,
  FaceMatchingIdentityRow,
} from '@/services/api/face-matching'

const lessOne = (count: number, applies = true) => Math.max(0, count - (applies ? 1 : 0))

/** The detail as it looks right after a confirm or a reject of *faceId*. */
export function withoutCandidate(
  detail: FaceMatchingIdentityDetail,
  faceId: string
): FaceMatchingIdentityDetail {
  const removed = detail.candidates.find(candidate => candidate.face_id === faceId)
  if (!removed) return detail
  return {
    ...detail,
    candidates: detail.candidates.filter(candidate => candidate.face_id !== faceId),
    total: lessOne(detail.total),
    pending_total: lessOne(detail.pending_total),
    suggested_matches: lessOne(detail.suggested_matches, removed.suggested_match),
  }
}

/** The detail with *faceId* flipped in or out of the reference set. */
export function withReference(
  detail: FaceMatchingIdentityDetail,
  faceId: string,
  reference: boolean
): FaceMatchingIdentityDetail {
  return {
    ...detail,
    references: detail.references.map(item =>
      item.face_id === faceId ? { ...item, reference } : item
    ),
  }
}

/** The identity list with one pending suggestion taken off *personId*'s row. */
export function withoutPending(
  rows: FaceMatchingIdentityRow[],
  personId: string,
  suggestedMatch: boolean
): FaceMatchingIdentityRow[] {
  return rows.map(row =>
    row.person_id === personId
      ? {
          ...row,
          pending_total: lessOne(row.pending_total),
          suggested_matches: lessOne(row.suggested_matches, suggestedMatch),
        }
      : row
  )
}
